import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter, Heart, Send } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../context/LanguageContext'
import { motion } from 'framer-motion'
import { useState } from 'react'
import emailjs from '@emailjs/browser'

const Footer = () => {
  const { t } = useLanguage()
  const currentYear = new Date().getFullYear()
  const [email, setEmail] = useState('')
  const [newsletterStatus, setNewsletterStatus] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setNewsletterStatus(null)

    try {
      // Send newsletter subscription via EmailJS
      await emailjs.send(
        'service_tn8xlnj',
        'template_anw5r65',
        {
          user_email: email,
          message: `New newsletter subscription from: ${email}`,
          user_name: 'Newsletter Subscriber'
        },
        'Ac73yOgPaJV0tu4Ri'
      )
      
      setNewsletterStatus('success')
      setEmail('')
      setTimeout(() => setNewsletterStatus(null), 3000)
    } catch (error) {
      console.error('Newsletter error:', error)
      setNewsletterStatus('error')
      setTimeout(() => setNewsletterStatus(null), 3000)
    } finally {
      setIsSubmitting(false)
    }
  }

  const footerLinks = {
    company: [
      { label: t('footer.about'), path: '/about' },
      { label: t('footer.services'), path: '/services' },
      { label: t('footer.portfolio'), path: '/portfolio' },
      { label: t('footer.blog'), path: '/blog' },
    ],
    services: [
      { label: t('footer.contentMarketing'), path: '/services' },
      { label: t('footer.socialMedia'), path: '/services' },
      { label: t('footer.branding'), path: '/services' },
      { label: t('footer.seo'), path: '/services' },
    ],
  }

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/contenta.al', label: 'Instagram', active: true },
    { icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61586035932543', label: 'Facebook', active: true },
    { icon: Linkedin, href: '#', label: 'LinkedIn (Coming Soon)', active: false },
    { icon: Twitter, href: '#', label: 'Twitter (Coming Soon)', active: false },
  ]

  return (
    <footer className="bg-gradient-to-br from-dark via-bronze-900 to-dark text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Contenta Logo" 
                className="w-12 h-12 object-contain drop-shadow-2xl"
              />
              <span className="text-2xl font-display font-bold">
                Contenta<span className="text-gold-400">.al</span>
              </span>
            </div>
            <p className="text-beige-200 leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target={social.active ? "_blank" : "_self"}
                  rel={social.active ? "noopener noreferrer" : undefined}
                  onClick={(e) => !social.active && e.preventDefault()}
                  className={`w-10 h-10 rounded-full flex items-center justify-center transform transition-all duration-300 ${
                    social.active 
                      ? 'bg-white/10 hover:bg-gradient-gold hover:scale-110 cursor-pointer' 
                      : 'bg-white/5 opacity-40 cursor-not-allowed'
                  }`}
                  aria-label={social.label}
                  title={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-xl font-display font-bold mb-6 text-gold-400">
              {t('footer.company')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-beige-200 hover:text-gold-400 transition-colors duration-300 inline-block hover:translate-x-2 transform"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-xl font-display font-bold mb-6 text-gold-400">
              {t('footer.services')}
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-beige-200 hover:text-gold-400 transition-colors duration-300 inline-block hover:translate-x-2 transform"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-xl font-display font-bold mb-6 text-gold-400">
              {t('footer.contact')}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                <a
                  href="mailto:info@contenta.marketing"
                  className="text-beige-200 hover:text-gold-400 transition-colors duration-300"
                >
                  info@contenta.marketing
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                <a
                  href="tel:+355695658665"
                  className="text-beige-200 hover:text-gold-400 transition-colors duration-300"
                >
                  +355 69 565 8665
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold-400 mt-1 flex-shrink-0" />
                <span className="text-beige-200">
                  Tirana, Albania
                </span>
              </li>
            </ul>
          </motion.div>

          {/* Newsletter Column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            <h3 className="text-xl font-display font-bold mb-6 text-gold-400">
              {t('footer.newsletter')}
            </h3>
            <p className="text-beige-200 mb-4 text-sm">
              {t('footer.newsletterText')}
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <div className="relative">
                <input
                  type="email"
                  required
                  disabled={isSubmitting}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-gold-500/20 text-cream placeholder-beige-300 focus:outline-none focus:border-gold-500 transition-colors disabled:opacity-50"
                />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-4 py-2 bg-gradient-gold text-dark font-semibold rounded-lg hover:shadow-glow transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                {isSubmitting ? t('footer.subscribing') : t('footer.subscribe')}
              </button>
              {newsletterStatus === 'success' && (
                <p className="text-green-400 text-sm">{t('footer.subscribeSuccess')}</p>
              )}
              {newsletterStatus === 'error' && (
                <p className="text-red-400 text-sm">{t('footer.subscribeError')}</p>
              )}
            </form>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-beige-300 text-sm text-center md:text-left">
              &copy; {currentYear} Contenta.al. {t('footer.rights')}
            </p>
            <p className="text-beige-300 text-sm flex items-center">
              {t('footer.made')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
